import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useRef, useState } from "react";

const SCRIPT_ID = "google-identity-services";
const SCRIPT_SRC = "https://accounts.google.com/gsi/client";

let scriptPromise;
let initializedClientId;
let activeCredentialHandler;

const loadGoogleIdentityServices = () => {
  if (window.google?.accounts?.id) return Promise.resolve(window.google);

  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const existing = document.getElementById(SCRIPT_ID);
      const script = existing || document.createElement("script");

      const loaded = () => {
        if (window.google?.accounts?.id) resolve(window.google);
        else reject(new Error(translateLabel("Google Identity Services did not initialize")));
      };
      const failed = () => reject(new Error(translateLabel("Google Identity Services could not be loaded")));

      script.addEventListener("load", loaded, { once: true });
      script.addEventListener("error", failed, { once: true });

      if (!existing) {
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    });
  }

  return scriptPromise;
};

const initializeGoogleIdentity = (clientId) => {
  if (initializedClientId && initializedClientId !== clientId) {
    throw new Error(translateLabel("Google Identity Services is already initialized with another client ID"));
  }
  if (initializedClientId) return;

  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: (response) => {
      if (response?.credential) activeCredentialHandler?.(response.credential);
    },
    use_fedcm_for_prompt: true,
  });
  initializedClientId = clientId;
};

export default function GoogleSignInButton({ onCredential, disabled = false }) {
  const containerRef = useRef(null);
  const handlerRef = useRef(onCredential);
  const disabledRef = useRef(disabled);
  const [loadError, setLoadError] = useState(false);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim();

  useEffect(() => {
    handlerRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  useEffect(() => {
    if (!clientId) return undefined;

    let active = true;
    const handler = (credential) => {
      if (!active || disabledRef.current) return;
      handlerRef.current?.(credential);
    };

    loadGoogleIdentityServices()
      .then(() => {
        if (!active || !containerRef.current) return;
        initializeGoogleIdentity(clientId);
        activeCredentialHandler = handler;

        const width = Math.max(240, Math.min(400, Math.round(containerRef.current.clientWidth || 360)));
        window.google.accounts.id.renderButton(containerRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          shape: "pill",
          text: "continue_with",
          width,
        });
      })
      .catch(() => {
        if (active) setLoadError(true);
      });

    return () => {
      active = false;
      if (activeCredentialHandler === handler) activeCredentialHandler = undefined;
    };
  }, [clientId]);

  if (!clientId) return null;

  return (
    <div
      className={`mt-5 ${disabled ? "pointer-events-none opacity-60" : ""}`}
      data-testid="google-sign-in"
    >
      <div className="flex min-h-11 w-full justify-center" ref={containerRef} />
      {loadError && (
        <p className="mt-2 text-center text-xs text-zinc-500">
          Google sign-in is temporarily unavailable. Use email and password instead.
        </p>
      )}
    </div>
  );
}
